// ==================== OUR CERTIFICATIONS PAGE COMPONENT ====================

import React, { useEffect, useState } from "react";
import Footer from "./Footer.js";
import Headerpage from "./Header2.js";
import { getData, serverURL } from "../../../Services/Fetchnodeservices.js";
import {
  Award,
  Shield,
  Star,
  CheckCircle,
  Clock,
  FileText,
  Calendar,
  ThumbsUp,
  HelpCircle,
  Trophy,
  Medal,
  Sparkles,
  Heart,
  Users,
  Globe,
  Phone,
  Mail,
  AlertCircle,
} from "lucide-react";

const OurCertifications = ({ isDark }) => {
  const [certificationData, setCertificationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const t = isDark
    ? {
        bg: "#0a0c10",
        cardBg: "#141824",
        text: "#e2e8f0",
        textSecondary: "#94a3b8",
        border: "rgba(255,255,255,0.08)",
        heading: "#ffffff",
        code: "#1e293b",
        accent: "#8b5cf6",
        accentLight: "#a78bfa",
      }
    : {
        bg: "#f8fafc",
        cardBg: "#ffffff",
        text: "#334155",
        textSecondary: "#64748b",
        border: "rgba(0,0,0,0.1)",
        heading: "#0f172a",
        code: "#f1f5f9",
        accent: "#8b5cf6",
        accentLight: "#c4b5fd",
      };

  const fetchCertifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("OurCertifications/OurCertifications");
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        setCertificationData(response.data);
      } else {
        setError("Certifications not found");
      }
    } catch (err) {
      console.error("Error fetching certifications:", err);
      setError("Failed to load Certifications. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertifications();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Certification highlights data
  const certifications = [
    {
      icon: <Trophy size={32} />,
      title: "Quality Certified",
      description: "ISO 9001:2021 Certified",
      color: "#f59e0b",
    },
    {
      icon: <Shield size={32} />,
      title: "Security Certified",
      description: "ISO 27001:2022 Certified",
      color: "#10b981",
    },
    {
      icon: <Medal size={32} />,
      title: "Excellence Award",
      description: "Best Delivery Service 2024",
      color: "#ef4444",
    },
    {
      icon: <Users size={32} />,
      title: "Customer Trust",
      description: "4.9/5 Customer Rating",
      color: "#8b5cf6",
    },
    {
      icon: <Globe size={32} />,
      title: "Global Standards",
      description: "International Quality Standards",
      color: "#3b82f6",
    },
    {
      icon: <Heart size={32} />,
      title: "Ethical Business",
      description: "Ethical Commerce Certified",
      color: "#ec4899",
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "60px",
                height: "60px",
                border: `3px solid ${t.border}`,
                borderTopColor: t.accent,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <p style={{ color: t.textSecondary }}>Loading Our Certifications...</p>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            padding: "20px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              backgroundColor: t.cardBg,
              padding: "40px",
              borderRadius: "20px",
              border: `1px solid ${t.border}`,
              maxWidth: "500px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <AlertCircle size={40} color="#ef4444" />
            </div>
            <h2 style={{ color: t.heading, marginBottom: "12px" }}>
              Certifications Not Found
            </h2>
            <p style={{ color: t.textSecondary, marginBottom: "24px" }}>
              {error}
            </p>
            <button
              onClick={fetchCertifications}
              style={{
                padding: "12px 24px",
                backgroundColor: t.accent,
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .certifications-content {
          word-wrap: break-word;
        }
        .certifications-content h1 {
          font-size: 2rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 700;
        }
        .certifications-content h2 {
          font-size: 1.5rem;
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
          border-left: 4px solid ${t.accent};
          padding-left: 1rem;
        }
        .certifications-content h3 {
          font-size: 1.25rem;
          margin-top: 1.2em;
          margin-bottom: 0.5em;
          color: ${t.heading};
          font-weight: 600;
        }
        .certifications-content p {
          line-height: 1.8;
          margin-bottom: 1rem;
          color: ${t.text};
        }
        .certifications-content ul,
        .certifications-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: ${t.text};
        }
        .certifications-content li {
          margin: 0.5rem 0;
          line-height: 1.7;
        }
        .certifications-content hr {
          margin: 2rem 0;
          border: none;
          height: 1px;
          background: ${t.border};
        }
        .certifications-content a {
          color: ${t.accent};
          text-decoration: none;
        }
        .certifications-content a:hover {
          text-decoration: underline;
        }
        .certifications-content strong {
          color: ${t.heading};
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .certifications-content h1 {
            font-size: 1.5rem;
          }
          .certifications-content h2 {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <div
        style={{
          fontFamily: "'Poppins', sans-serif",
          background: t.bg,
          minHeight: "100vh",
        }}
      >
        <Headerpage />

        {/* Hero Section */}
        <section
          style={{
            position: "relative",
            minHeight: "400px",
            padding: "80px 20px",
            textAlign: "center",
            background: `linear-gradient(135deg, #0b0417 0%, #1a0b3a 40%, ${t.accent} 100%)`,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {/* Animated Background Elements */}
          <div
            style={{
              position: "absolute",
              width: "400px",
              height: "400px",
              background: "rgba(139,92,246,0.15)",
              filter: "blur(70px)",
              borderRadius: "50%",
              top: "-200px",
              left: "-150px",
              animation: "float 6s ease-in-out infinite",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "350px",
              height: "350px",
              background: "rgba(139,92,246,0.12)",
              filter: "blur(60px)",
              borderRadius: "50%",
              bottom: "-180px",
              right: "-120px",
              animation: "float 8s ease-in-out infinite reverse",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "200px",
              height: "200px",
              background: "rgba(255,255,255,0.05)",
              filter: "blur(40px)",
              borderRadius: "50%",
              top: "30%",
              right: "15%",
              animation: "pulse 4s ease-in-out infinite",
            }}
          />

          <style>
            {`
              @keyframes float {
                0%, 100% { transform: translateY(0px); }
                50% { transform: translateY(-20px); }
              }
              @keyframes pulse {
                0%, 100% { transform: scale(1); opacity: 0.5; }
                50% { transform: scale(1.2); opacity: 0.8; }
              }
            `}
          </style>

          <div
            style={{
              position: "relative",
              zIndex: 2,
              maxWidth: "900px",
            }}
          >
            {certificationData?.page_thumbnail && (
              <div style={{ marginBottom: "24px" }}>
                <img
                  src={`${serverURL}/images/${certificationData.page_thumbnail}`}
                  alt={certificationData.page_title}
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid white",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                    animation: "pulse 3s ease-in-out infinite",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}

            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                padding: "8px 24px",
                borderRadius: "40px",
                marginBottom: "24px",
              }}
            >
              <Award size={18} />
              <span style={{ fontSize: "14px", fontWeight: "500" }}>
                Our Certifications & Achievements
              </span>
            </div>

            <h1
              style={{
                fontSize: "clamp(42px, 7vw, 64px)",
                fontWeight: "800",
                marginBottom: "20px",
                background: "linear-gradient(135deg, #fff 0%, #c4b5fd 50%, #a78bfa 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {certificationData?.page_title || "Our Certifications"}
            </h1>

            <p
              style={{
                fontSize: "18px",
                opacity: 0.95,
                maxWidth: "700px",
                margin: "0 auto",
                lineHeight: "1.6",
              }}
            >
              {certificationData?.page_description?.replace(/<[^>]*>/g, '') || 
                "Showcasing our commitment to excellence through industry-recognized certifications and standards"}
            </p>

            <div
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "32px",
                fontSize: "14px",
                opacity: 0.85,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Calendar size={16} />
                <span>Updated: {formatDate(certificationData?.updated_at)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CheckCircle size={16} />
                <span>Verified Certifications</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section
          style={{
            padding: "80px 20px",
            position: "relative",
            zIndex: 10,
          }}
        >
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
            }}
          >
            {/* Certifications Grid Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: "30px",
                marginBottom: "60px",
              }}
            >
              {certifications.map((cert, index) => (
                <div
                  key={index}
                  style={{
                    background: t.cardBg,
                    padding: "32px",
                    borderRadius: "24px",
                    border: `1px solid ${t.border}`,
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      background: `${cert.color}15`,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 20px",
                      color: cert.color,
                    }}
                  >
                    {cert.icon}
                  </div>
                  <h3 style={{ color: t.heading, fontSize: "20px", marginBottom: "12px", fontWeight: "700" }}>
                    {cert.title}
                  </h3>
                  <p style={{ color: t.textSecondary, fontSize: "14px", lineHeight: "1.6" }}>
                    {cert.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Main Content Card */}
            <div
              style={{
                backgroundColor: t.cardBg,
                borderRadius: "32px",
                border: `1px solid ${t.border}`,
                overflow: "hidden",
                boxShadow: isDark
                  ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                  : "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ padding: "48px" }}>
                <div
                  className="certifications-content"
                  dangerouslySetInnerHTML={{
                    __html: certificationData?.page_description || "<p>No content available</p>",
                  }}
                />

                {/* Footer Info */}
                <div
                  style={{
                    marginTop: "48px",
                    paddingTop: "24px",
                    borderTop: `1px solid ${t.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "16px",
                    fontSize: "13px",
                    color: t.textSecondary,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FileText size={16} />
                    <span>Page ID: {certificationData?.id}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Calendar size={16} />
                    <span>Created: {formatDate(certificationData?.created_at)}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CheckCircle size={16} />
                    <span>Status: {certificationData?.status === 1 ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us Section */}
            <div
              style={{
                marginTop: "60px",
                background: `linear-gradient(135deg, ${t.accent}10, ${t.accent}05)`,
                borderRadius: "32px",
                padding: "48px",
                textAlign: "center",
                border: `1px solid ${t.border}`,
              }}
            >
              <div
                style={{
                  width: "70px",
                  height: "70px",
                  background: `${t.accent}20`,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <ThumbsUp size={32} color={t.accent} />
              </div>
              <h2
                style={{
                  color: t.heading,
                  fontSize: "32px",
                  marginBottom: "16px",
                  fontWeight: "700",
                }}
              >
                Why Choose Zaptodoor?
              </h2>
              <p
                style={{
                  color: t.textSecondary,
                  maxWidth: "700px",
                  margin: "0 auto",
                  fontSize: "16px",
                  lineHeight: "1.7",
                }}
              >
                We are committed to providing the highest quality delivery services with 
                industry-recognized certifications and standards that ensure reliability, 
                security, and customer satisfaction.
              </p>
            </div>

            {/* Need Help Section */}
            <div
              style={{
                background: t.cardBg,
                borderRadius: "28px",
                padding: "40px",
                marginTop: "60px",
                textAlign: "center",
                border: `1px solid ${t.border}`,
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: `${t.accent}20`,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <HelpCircle size={28} color={t.accent} />
              </div>
              <h3
                style={{
                  color: t.heading,
                  fontSize: "24px",
                  marginBottom: "12px",
                }}
              >
                Have Questions About Our Certifications?
              </h3>
              <p
                style={{
                  color: t.textSecondary,
                  marginBottom: "28px",
                  fontSize: "15px",
                }}
              >
                Contact us to learn more about our quality standards and certifications
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <a
                  href="tel:+919200018690"
                  style={{
                    background: t.accent,
                    color: "white",
                    padding: "12px 32px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 10px 20px rgba(0,0,0,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  <Phone size={18} />
                  Call Now
                </a>
                <a
                  href="mailto:info@zaptodoor.com"
                  style={{
                    background: "transparent",
                    color: t.text,
                    padding: "12px 32px",
                    borderRadius: "40px",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    border: `1px solid ${t.border}`,
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = t.accent;
                    e.target.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = t.text;
                  }}
                >
                  <Mail size={18} />
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default OurCertifications;